// Copyright (c) Docugami, Inc. All rights reserved.

import { selectChunk, setEditTokenRange } from "hooks/chunkSelection";
import { DgmlInstancesSingleton } from "hooks/documentDgml";
import { TokenRange } from "networking/api";
import { ChunkRenderComponent, createChunkRenderComponent } from "sdv/components/ChunkRenderComponent";
import { getEnclosingHyperlineForRange } from "sdv/components/ControlComponent";
import { CursorComponent } from "sdv/components/CursorComponent";
import { HighlighterComponent } from "sdv/components/HighlighterComponent";
import { MultiSelectComponent } from "sdv/components/MultiSelectComponent";
import { ResizeComponent, TokenSnapBehavior } from "sdv/components/ResizeComponent";
import { TokenPositionComponent } from "sdv/components/TokenPositionComponent";
import { TokenRangeComponent } from "sdv/components/TokenRangeComponent";
import { TokenRangeInfoComponent } from "sdv/components/TokenRangeInfoComponent";
import { ComponentId, tagComponent } from "sdv/engine/Component";
import { Entity, IEntityManager } from "sdv/engine/Entity";
import { createActiveHighlightEntity } from "sdv/engine/EntityFactory";
import { IExternalCallbacks } from "sdv/ExternalCallbacks";
import { ActionId } from "sdv/input/Actions";
import { InputState } from "sdv/input/InputController";
import { InteractEvent } from "sdv/InteractEvent";
import { Mode } from "sdv/Mode";
import { CanvasSelectionMode } from "store/documents/types";
import { TokenRangeUtils } from "utils/TokenRangeUtils";
import { ISystem, Queries } from "../engine/Engine";
import { IResources } from "../Resources";

/**
 * Manages the lifetime of "highlight" operations where the user is
 * creating new chunks.
 */
export class HighlighterSystem implements ISystem {

    get updateEnabled() { return true; }

    queries: Queries = {
        allHighlights: { types: [ComponentId.Highlighter] },
        validHighlights: { types: [ComponentId.TokenRange, ComponentId.Highlighter] },
        activeHighlights: { types: [ComponentId.Drag, ComponentId.TokenPosition, ComponentId.TokenRange, ComponentId.Highlighter] },
        highlightAction: { types: [ComponentId.Drag, ComponentId.TokenPosition, ComponentId.SnapToToken, ComponentId.Highlighter] },
        selected: { types: [ComponentId.Selected] },
        root: { types: [ComponentId.RootControl, ComponentId.Control, ComponentId.Active] },
        cursor: { types: [ComponentId.Cursor] }
    }

    input!: InputState;
    entities!: IEntityManager;
    resources!: IResources;
    callbacks!: IExternalCallbacks;

    _multiSelect: boolean = false;

    update(): void {
        this._multiSelect = this.resources.get("multiSelect").value;

        this.handleModeDisabled();
        this.startHighlight();
        this.createHighlightControl();
        this.updateTokenRange();
        this.endHighlight();
        this.handleExternalSelectionChange();
    }

    private handleModeDisabled() {
        if (this.resources.check(["tool", "multiSelect"]) && this.resources.get("tool").value !== CanvasSelectionMode.Highlight && !this._multiSelect) {
            this.queries.allHighlights.query!.entities.forEach(e => e.destroy());
        }
    }

    private handleExternalSelectionChange(): void {
        const tool = this.resources.get("tool").value;
        if (tool !== CanvasSelectionMode.Highlight) {
            return;
        }

        // Handle the selected span id being changed externally when there is an active highlight
        const selectedEntity = this.resources.get("selectedEntityName");
        if (!selectedEntity.updated || !selectedEntity.value) {
            return;
        }

        const getHighlightSpanId = (entity: Entity) => {
            const tokenRange = entity.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;
            return DgmlInstancesSingleton.getSpanIdForTokenRange(this.resources.get('documentId').value, tokenRange);
        }

        const highlight = this.queries.activeHighlights.query!.entities[0]
        if (!highlight || selectedEntity.value !== getHighlightSpanId(highlight)) {
            if (!!highlight) {
                markHighlightEntityInactive(highlight);
            }
            setEditTokenRange();
        }
    }

    private startHighlight(): void {
        const tool = this.resources.get("tool").value;
        if (tool !== CanvasSelectionMode.Highlight) {
            return;
        }

        const selectState = this.input.getButton(ActionId.Select);
        if (!selectState.handled && selectState.isDown) {
            selectState.handle();
            if (this.queries.allHighlights.query!.any()) {
                if (!this._multiSelect) {
                    // Only one highlight is supported at a time
                    this.queries.allHighlights.query!.entities.forEach(e => e.destroy());
                }
                else {
                    // Remove active marker from previous highlight chunk, and reset the edit tokens state
                    this.queries.allHighlights.query!.entities.forEach(markHighlightEntityInactive);
                    setEditTokenRange();
                }
            }

            // Ensure any currently selected entity is deselected
            selectChunk(undefined);

            // Create the new highlight entity
            this.resources.set("mode", Mode.Edit);
            createActiveHighlightEntity(this.entities);

            if (!!this.callbacks.onInteraction) {
                this.callbacks.onInteraction(InteractEvent.Resize);
            }
        }
        else if (!selectState.handled && !selectState.isPressed && !this.queries.validHighlights.query!.any()) {
            // Mouse has been released and there are no valid highlights
            selectState.handle();
            selectChunk(undefined);
        }
    }

    private createHighlightControl(): void {
        const highlightEntity = this.queries.highlightAction.query!.single();
        const cursorComponent = this.queries.cursor.query!.single()?.getComponent<CursorComponent>(ComponentId.Cursor);
        if (!highlightEntity || !!highlightEntity.hasComponent(ComponentId.TokenRange) || !cursorComponent) {
            return;
        }

        const tokenPosition = highlightEntity.getComponent<TokenPositionComponent>(ComponentId.TokenPosition)!;
        const rootTokenRange = this.queries.root.query!.single()!.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;
        if (tokenPosition.token !== undefined && TokenRangeUtils.contains(rootTokenRange, tokenPosition.token) && this.validateTokenPosition(tokenPosition.token)) {
            // We have a valid token under the mouse so let's create the control to render
            // Add a token range for the current token
            const tokenRange = { fromTok: tokenPosition.token, toTok: tokenPosition.token + 1 };
            if (!!cursorComponent.editTokens?.length) {
                // There are edit tokens that share the same token position - update the token range to include them
                tokenRange.fromTok = Math.min(cursorComponent.editTokens[0], tokenPosition.token);
                tokenRange.toTok = Math.max(cursorComponent.editTokens[cursorComponent.editTokens.length - 1], tokenPosition.token) + 1;
            }

            this.constructHighlightControlFromRange(highlightEntity, tokenRange);

            const enclosingHyperlineId = getEnclosingHyperlineForRange(tokenRange, this.queries.root.query!.single(), this.entities)?.name;
            highlightEntity.addComponent<ResizeComponent>({
                typeId: ComponentId.Resize,
                ops: [{ tokenRange: tokenRange, info: { isSubStructural: true, fromTokHyperlineId: enclosingHyperlineId, toTokHyperlineId: enclosingHyperlineId } }],
                isCreating: true,
                snapBehavior: TokenSnapBehavior.Token
            });

            highlightEntity.addComponent(tagComponent(ComponentId.Selected));
        }
    }

    // Validates that new highlights are not started on top of existing ones
    private validateTokenPosition(token: number) {
        return !this._multiSelect || !this.queries.validHighlights.query!.entities.some(e =>
            TokenRangeUtils.contains(e.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange, token));
    }

    private constructHighlightControlFromRange(highlightEntity: Entity, tokenRange: TokenRange) {
        const highlighter = highlightEntity.getComponent<HighlighterComponent>(ComponentId.Highlighter)!;
        highlighter.startToken = tokenRange.fromTok;
        highlightEntity.addComponent<TokenRangeComponent>({
            typeId: ComponentId.TokenRange,
            tokenRange: tokenRange
        });

        // Render this entity as a chunk.
        highlightEntity.addComponent<ChunkRenderComponent>(createChunkRenderComponent());
        highlightEntity.addComponent<TokenRangeInfoComponent>({ typeId: ComponentId.TokenRangeInfo });
    }

    private updateTokenRange() {
        for (const entity of this.queries.activeHighlights.query!.entities) {
            const startToken = entity.getComponent<HighlighterComponent>(ComponentId.Highlighter)!.startToken;
            const tokenPosition = entity.getComponent<TokenPositionComponent>(ComponentId.TokenPosition)!.token;
            if (startToken === undefined || tokenPosition === undefined) {
                continue;
            }

            const resizeComp = entity.getComponent<ResizeComponent>(ComponentId.Resize);
            if (!!resizeComp) {
                resizeComp.isFromTok = tokenPosition < startToken;
            }
        }
    }

    private endHighlight(): void {
        const selectState = this.input.getButton(ActionId.Select);
        if (!selectState.isPressed && this.queries.highlightAction.query!.any()) {
            this.queries.highlightAction.query!.entities.forEach(e => {
                e.removeComponent(ComponentId.Drag);
                const resizeComponent = e.getComponent<ResizeComponent>(ComponentId.Resize);
                if (!!resizeComponent) {
                    resizeComponent.isCreating = false;
                }

                if (e.hasComponent(ComponentId.Selected)) {
                    const tokenRange = e.getComponent<TokenRangeComponent>(ComponentId.TokenRange)!.tokenRange;
                    setEditTokenRange(tokenRange);

                    if (this._multiSelect) {
                        e.ensureComponent<MultiSelectComponent>({
                            typeId: ComponentId.MultiSelect,
                            spanId: DgmlInstancesSingleton.getSpanIdForTokenRange(this.resources.get('documentId').value, tokenRange)
                        }, true)
                    }
                }
            });
        }
    }
}

function markHighlightEntityInactive(entity: Entity): void {
    entity
        .removeComponent(ComponentId.Drag)
        .removeComponent(ComponentId.TokenPosition)
        .removeComponent(ComponentId.SnapToToken)
        .removeComponent(ComponentId.Resize)
        .removeComponent(ComponentId.Selected);
}